import React, { memo, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  TextField,
  Rating,
} from '@mui/material';
import moment from 'moment';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import { environment } from '../../../environments/environment';
import { useTranslation } from 'react-i18next';
import { handleToaster } from '../../Utils/UtilsFunctions';

const OrderItem = memo(
  ({
    order,
    onViewDetails,
    onReorder,
    onCancelOrder,
    onRateOrder,
    checkLicenses,
  }) => {
    const [t] = useTranslation();
    const {
      order_id,
      deliveryOption,
      store,
      order_total,
      order_items,
      created_at,
      order_status,
      cancellation_time,
      rated,
    } = order;

    // Local state for dialogs
    const [openReorder, setOpenReorder] = useState(false);
    const [openCancel, setOpenCancel] = useState(false);
    const [openRate, setOpenRate] = useState(false);
    const [feedback, setFeedback] = useState('');
    const [rating, setRating] = useState(5);
    const [feedbackError, setFeedbackError] = useState('');

    // Memoized date calculations
    const createdDate = useMemo(
      () => new Date(created_at?.seconds * 1000),
      [created_at?.seconds]
    );

    const cancelDate = useMemo(
      () =>
        cancellation_time ? new Date(cancellation_time?.seconds * 1000) : null,
      [cancellation_time?.seconds]
    );

    // Memoized status configuration
    const statusConfig = useMemo(() => {
      const configs = {
        pending: {
          color: '#ff9800',
          icon: FiberManualRecordIcon,
          label: 'Pending',
        },
        confirmed: {
          color: '#2196f3',
          icon: FiberManualRecordIcon,
          label: 'Confirmed',
        },
        preparing: {
          color: '#ff5722',
          icon: FiberManualRecordIcon,
          label: 'Preparing',
        },
        ready: {
          color: '#4caf50',
          icon: CheckCircleOutlineIcon,
          label: 'Ready',
        },
        completed: {
          color: '#4caf50',
          icon: CheckCircleOutlineIcon,
          label: 'Completed',
        },
        cancelled: {
          color: '#f44336',
          icon: FiberManualRecordIcon,
          label: 'Cancelled',
        },
      };
      return configs[order_status?.toLowerCase()] || configs.pending;
    }, [order_status]);

    // Memoized handlers
    const handleReorderClick = useCallback(() => {
      setOpenReorder(true);
    }, []);

    const handleConfirmReorder = useCallback(() => {
      onReorder?.(order);
      setOpenReorder(false);
    }, [onReorder, order]);

    const handleCancelClick = useCallback(() => {
      setOpenCancel(true);
    }, []);

    const handleConfirmCancel = useCallback(() => {
      onCancelOrder?.(order_id);
      setOpenCancel(false);
    }, [onCancelOrder, order_id]);

    const handleRateClick = useCallback(() => {
      setOpenRate(true);
    }, []);

    const handleSubmitRating = useCallback(() => {
      if (rating < 4 && !feedback.trim()) {
        setFeedbackError(t('profilee.feedbackrequired'));
        return;
      }

      onRateOrder?.({ orderId: order_id, rating, feedback });
      setOpenRate(false);
      setFeedback('');
      setRating(5);
      setFeedbackError('');
    }, [rating, feedback, onRateOrder, order_id, t]);

    const handleViewDetails = useCallback(() => {
      onViewDetails?.(order);
    }, [onViewDetails, order]);

    // Close handlers
    const handleCloseReorder = useCallback(() => setOpenReorder(false), []);
    const handleCloseCancel = useCallback(() => setOpenCancel(false), []);
    const handleCloseRate = useCallback(() => {
      setOpenRate(false);
      setFeedback('');
      setFeedbackError('');
    }, []);

    // Memoized order summary
    const orderSummary = useMemo(() => {
      const itemCount = order_items?.length || 0;
      const firstItem = order_items?.[0];
      return {
        itemCount,
        firstItemName: firstItem?.name || 'Items',
        totalAmount: order_total || 0,
      };
    }, [order_items, order_total]);

    // Check if order can be cancelled (within cancellation window)
    const canCancel = useMemo(() => {
      if (!created_at?.seconds || order_status?.toLowerCase() === 'cancelled')
        return false;
      const orderTime = new Date(created_at.seconds * 1000);
      const now = new Date();
      const timeDiff = (now - orderTime) / (1000 * 60); // minutes
      return (
        timeDiff <= 30 &&
        ['pending', 'confirmed'].includes(order_status?.toLowerCase())
      );
    }, [created_at?.seconds, order_status]);

    if (!checkLicenses?.('orderhistory')) {
      return null;
    }

    return (
      <>
        <Card
          sx={{
            mb: 3,
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            '&:hover': {
              boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
            },
            transition: 'box-shadow 0.2s ease',
          }}
        >
          <CardContent sx={{ p: 3 }}>
            {/* Header */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                mb: 2,
              }}
            >
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: 'Jost, sans-serif',
                    fontWeight: 600,
                    fontSize: '18px',
                    mb: 0.5,
                  }}
                >
                  Order #{order_id?.slice(-6) || 'N/A'}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#666',
                    fontFamily: 'Jost, sans-serif',
                  }}
                >
                  {moment(createdDate).format('MMM DD, YYYY • hh:mm A')}
                </Typography>
              </Box>
              <Chip
                icon={React.createElement(statusConfig.icon, {
                  sx: { fontSize: 16 },
                })}
                label={statusConfig.label}
                sx={{
                  backgroundColor: `${statusConfig.color}15`,
                  color: statusConfig.color,
                  fontFamily: 'Jost, sans-serif',
                  fontWeight: 500,
                  '& .MuiChip-icon': {
                    color: statusConfig.color,
                  },
                }}
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Order Details */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Box>
                <Typography
                  variant="body1"
                  sx={{
                    fontFamily: 'Jost, sans-serif',
                    fontWeight: 500,
                    mb: 0.5,
                  }}
                >
                  {orderSummary.itemCount} Item
                  {orderSummary.itemCount !== 1 ? 's' : ''}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#666',
                    fontFamily: 'Jost, sans-serif',
                  }}
                >
                  {store?.businessName || 'Store'} •{' '}
                  {deliveryOption || 'Pickup'}
                </Typography>
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: 'Jost, sans-serif',
                  fontWeight: 600,
                  color: environment.mainColor,
                }}
              >
                ${orderSummary.totalAmount}
              </Typography>
            </Box>

            {/* Action Buttons */}
            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                onClick={handleViewDetails}
                sx={{
                  fontFamily: 'Jost, sans-serif',
                  textTransform: 'none',
                  borderColor: environment.mainColor,
                  color: environment.mainColor,
                  '&:hover': {
                    borderColor: environment.mainColor,
                    backgroundColor: 'rgba(255, 107, 53, 0.04)',
                  },
                }}
              >
                View Details
              </Button>

              <Button
                variant="contained"
                onClick={handleReorderClick}
                sx={{
                  fontFamily: 'Jost, sans-serif',
                  textTransform: 'none',
                  backgroundColor: environment.mainColor,
                  '&:hover': {
                    backgroundColor: environment.mainColor,
                    opacity: 0.9,
                  },
                }}
              >
                Reorder
              </Button>

              {canCancel && (
                <Button
                  variant="text"
                  onClick={handleCancelClick}
                  sx={{
                    fontFamily: 'Jost, sans-serif',
                    textTransform: 'none',
                    color: '#f44336',
                    '&:hover': {
                      backgroundColor: 'rgba(244, 67, 54, 0.04)',
                    },
                  }}
                >
                  Cancel
                </Button>
              )}

              {order_status?.toLowerCase() === 'completed' && !rated && (
                <Button
                  variant="text"
                  onClick={handleRateClick}
                  sx={{
                    fontFamily: 'Jost, sans-serif',
                    textTransform: 'none',
                    color: environment.mainColor,
                    '&:hover': {
                      backgroundColor: 'rgba(255, 107, 53, 0.04)',
                    },
                  }}
                >
                  Rate Order
                </Button>
              )}
            </Stack>
          </CardContent>
        </Card>

        {/* Reorder Confirmation Dialog */}
        <Dialog open={openReorder} onClose={handleCloseReorder}>
          <DialogContent>
            <DialogContentText sx={{ fontFamily: 'Jost, sans-serif' }}>
              Are you sure you want to reorder these items? This will replace
              your current cart.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseReorder}
              sx={{ fontFamily: 'Jost, sans-serif' }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmReorder}
              variant="contained"
              sx={{
                fontFamily: 'Jost, sans-serif',
                backgroundColor: environment.mainColor,
              }}
            >
              Confirm Reorder
            </Button>
          </DialogActions>
        </Dialog>

        {/* Cancel Order Dialog */}
        <Dialog open={openCancel} onClose={handleCloseCancel}>
          <DialogContent>
            <DialogContentText sx={{ fontFamily: 'Jost, sans-serif' }}>
              Are you sure you want to cancel this order? This action cannot be
              undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseCancel}
              sx={{ fontFamily: 'Jost, sans-serif' }}
            >
              Keep Order
            </Button>
            <Button
              onClick={handleConfirmCancel}
              variant="contained"
              color="error"
              sx={{ fontFamily: 'Jost, sans-serif' }}
            >
              Cancel Order
            </Button>
          </DialogActions>
        </Dialog>

        {/* Rate Order Dialog */}
        <Dialog
          open={openRate}
          onClose={handleCloseRate}
          maxWidth="sm"
          fullWidth
        >
          <DialogContent sx={{ p: 3 }}>
            <Typography
              variant="h6"
              sx={{
                fontFamily: 'Jost, sans-serif',
                mb: 2,
                textAlign: 'center',
              }}
            >
              Rate Your Order
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Rating
                value={rating}
                onChange={(_, newValue) => setRating(newValue || 5)}
                size="large"
              />
            </Box>
            {rating < 4 && (
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Please tell us what went wrong..."
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                error={!!feedbackError}
                helperText={feedbackError}
                sx={{ mt: 2 }}
              />
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button
              onClick={handleCloseRate}
              sx={{ fontFamily: 'Jost, sans-serif' }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitRating}
              variant="contained"
              sx={{
                fontFamily: 'Jost, sans-serif',
                backgroundColor: environment.mainColor,
              }}
            >
              Submit Rating
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
);

OrderItem.displayName = 'OrderItem';

export default OrderItem;
